import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MailX, Home, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { publicApi } from '../lib/api';
import { toast } from 'sonner';

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const [status, setStatus] = useState('loading'); // loading, success, error

  useEffect(() => {
    const handleUnsubscribe = async () => {
      if (!email) {
        setStatus('error');
        return;
      }

      try {
        await publicApi.unsubscribe(email);
        setStatus('success');
        toast.success('You have been successfully unsubscribed.');
      } catch (error) {
        console.error('Unsubscribe error:', error);
        setStatus('error');
        toast.error(error.response?.data?.detail || 'Failed to unsubscribe. Please try again later.');
      }
    };

    handleUnsubscribe();
  }, [email]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full glass-morphic rounded-3xl p-8 text-center space-y-6"
      >
        {status === 'loading' && (
          <>
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <Loader2 className="h-10 w-10 text-primary animate-spin" />
            </div>
            <h1 className="text-2xl font-semibold">Unsubscribing...</h1>
            <p className="text-muted-foreground">
              Please wait while we process your request.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
              <CheckCircle className="h-10 w-10 text-green-500" />
            </div>
            <h1 className="text-2xl font-semibold">Unsubscribed Successfully</h1>
            <p className="text-muted-foreground">
              We've removed <strong>{email}</strong> from our mailing list. You will no longer receive product notifications.
            </p>
            <div className="pt-4">
              <Button asChild className="w-full rounded-full h-12">
                <Link to="/">
                  <Home className="mr-2 h-4 w-4" />
                  Back to Home
                </Link>
              </Button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
              <AlertCircle className="h-10 w-10 text-destructive" />
            </div>
            <h1 className="text-2xl font-semibold">Something went wrong</h1>
            <p className="text-muted-foreground">
              {email 
                ? "We couldn't process your unsubscribe request at this time."
                : "Invalid unsubscribe link. No email address provided."}
            </p>
            <div className="pt-4 space-y-3">
              <Button asChild className="w-full rounded-full h-12">
                <Link to="/">
                  <Home className="mr-2 h-4 w-4" />
                  Back to Home
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full rounded-full h-12">
                <Link to="/contact">
                  Contact Support
                </Link>
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
